/**
 * InvoicePreview — printable invoice layout, used as both the
 * composer's right-rail live preview and the detail page's main render.
 *
 * The composer can render this as `chrome="plain"` because its right
 * rail owns the prototype card/header/summary chrome. Detail pages keep
 * the default card chrome.
 *
 * @see prototype/sales/invoice-new.html — `.preview-doc`
 */
import { Info } from "lucide-react";
import type { ReactNode } from "react";
import { Card, CardBody, StatusPill, type StatusTone } from "@flint/ui";
import { formatPaise } from "./lib/format-money.ts";
import { stateNameFromCode } from "./lib/state-codes.ts";
import type { ShimInvoiceTotals } from "./lib/gst-calc-shim.ts";

export interface InvoicePreviewSeller {
  name: string;
  addressLines: string[];
  gstin: string | null;
  state: string | null;
  /** PR-H — printed in the "Pay to" footer when set. */
  bankName?: string | null;
  bankAccountNumber?: string | null;
  bankIfsc?: string | null;
  bankUpiId?: string | null;
}

export interface InvoicePreviewCustomer {
  name: string;
  gstin: string | null;
  state: string | null;
  city: string | null;
}

export interface InvoicePreviewLine {
  position: number;
  description: string;
  hsn: string;
  qtyMilli: number;
  unit: string;
  ratePaise: bigint;
  lineTotalPaise: bigint;
  /**
   * Per-line GST rate in basis-points. Optional so legacy callers
   * compile, but surfaced in the prototype's meta-line ("HSN 7318 ·
   * 18% GST"). Passed through from the composer + detail page.
   */
  taxRateBps?: number;
}

export interface InvoicePreviewProps {
  seller: InvoicePreviewSeller;
  customer: InvoicePreviewCustomer;
  /** `null` for drafts, renders as "— pending —". */
  docNumber: string | null;
  issuedOn: string;
  dueOn: string;
  placeOfSupplyCode: string;
  lines: InvoicePreviewLine[];
  totals: ShimInvoiceTotals;
  customerNote?: string | undefined;
  /** Status pill in the title row. Defaults to `'Draft'` when `docNumber` is null. */
  status?: "draft" | "issued" | "partially_paid" | "paid" | "overdue" | "void" | undefined;
  /** Composer preview supplies its own outer card chrome. */
  chrome?: "card" | "plain" | undefined;
  /** Printed document label. Defaults to "Tax invoice"; proformas override it. */
  documentLabel?: string | undefined;
  /** Detail meta label. Defaults to "Invoice details"; proformas override it. */
  detailLabel?: string | undefined;
}

export function InvoicePreview({
  seller,
  customer,
  docNumber,
  issuedOn,
  dueOn,
  placeOfSupplyCode,
  lines,
  totals,
  customerNote,
  status,
  chrome = "card",
  documentLabel = "Tax invoice",
  detailLabel = "Invoice details",
}: InvoicePreviewProps) {
  const effectiveStatus = status ?? (docNumber ? "issued" : "draft");
  const content = (
    <div className="flex flex-col gap-4 text-xs leading-relaxed text-ink-2">
      <div className="flex items-start justify-between gap-4 border-b border-line pb-3.5">
        <div className="min-w-0 text-muted">
          <p className="mb-0.5 text-sm font-bold leading-tight text-ink">{seller.name}</p>
          {seller.addressLines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
          {seller.gstin ? <p className="mt-1 font-mono">GSTIN {seller.gstin}</p> : null}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            {documentLabel}
          </p>
          <p className="mt-1 font-mono text-sm font-bold text-ink">{docNumber ?? "— pending —"}</p>
          <StatusPill tone={statusTone(effectiveStatus)} size="sm">
            {statusLabel(effectiveStatus)}
          </StatusPill>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 border-b border-line pb-3.5 sm:grid-cols-2">
        <PreviewMetaCell label="Bill to">
          <strong className="text-ink">{customer.name}</strong>
          <br />
          <span>
            {customer.city || customer.state
              ? [customer.city, customer.state].filter(Boolean).join(", ")
              : "—"}
          </span>
          {customer.gstin ? (
            <>
              <br />
              <span className="font-mono text-muted">GSTIN {customer.gstin}</span>
            </>
          ) : null}
        </PreviewMetaCell>
        <PreviewMetaCell label={detailLabel} mono>
          Issued <strong>{formatPreviewDate(issuedOn)}</strong>
          <br />
          Due <strong>{formatPreviewDate(dueOn)}</strong>
          <br />
          Place{" "}
          <strong>
            {stateNameFromCode(placeOfSupplyCode) ?? "—"} ({placeOfSupplyCode})
          </strong>
        </PreviewMetaCell>
      </div>

      {/* Items table — 4 columns, matches the prototype's
       *  `.preview-items`. HSN + tax rate go into a meta-line under
       *  the description (not their own columns), and the Qty cell
       *  drops the unit suffix so the column stays narrow and
       *  numeric. */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wider text-muted">
              <th className="py-2 pr-2 font-semibold">Item</th>
              <th className="w-16 px-2 py-2 text-right font-semibold">Qty</th>
              <th className="w-20 px-2 py-2 text-right font-semibold">Rate</th>
              <th className="w-24 py-2 pl-2 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.position} className="border-b border-line last:border-0">
                <td className="py-2 pr-2 text-ink">
                  <p className="font-medium">{line.description || "—"}</p>
                  <p className="text-xs text-muted">
                    HSN {line.hsn || "—"}
                    {line.taxRateBps !== undefined
                      ? ` · ${(line.taxRateBps / 100).toFixed(0)}% GST`
                      : " · GST"}
                  </p>
                </td>
                <td className="px-2 py-2 text-right font-mono text-ink-2 tabular-nums">
                  {(line.qtyMilli / 1000).toLocaleString("en-IN")}
                </td>
                <td className="px-2 py-2 text-right font-mono text-ink-2 tabular-nums">
                  {formatPaise(line.ratePaise)}
                </td>
                <td className="py-2 pl-2 text-right font-mono text-ink tabular-nums">
                  {formatPaise(line.lineTotalPaise)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals — always render Discount and Round-off rows so the
       *  preview keeps its structural shape across drafts whether or
       *  not those values are zero. Mirrors prototype `.preview-totals`. */}
      <dl className="ml-auto flex w-full max-w-[220px] flex-col gap-0 border-t border-line pt-3 text-xs">
        <TotalRow label="Subtotal" value={totals.subtotalPaise} />
        <TotalRow label="Discount" value={totals.discountTotalPaise} negative />
        <TotalRow label="Taxable value" value={totals.taxableValuePaise} />
        {totals.taxMode === "intra" ? (
          <>
            <TotalRow label="CGST" value={totals.cgstPaise} />
            <TotalRow label="SGST" value={totals.sgstPaise} />
          </>
        ) : (
          <TotalRow label="IGST" value={totals.igstPaise} />
        )}
        <TotalRow label="Round-off" value={totals.roundOffPaise} signed />
        <div className="mt-1.5 flex items-center justify-between border-t border-line pt-2 text-sm font-bold text-ink">
          <dt>Grand total</dt>
          <dd className="font-mono text-sm">₹{formatPaise(totals.grandTotalPaise)}</dd>
        </div>
      </dl>

      {/* Tax-mode notice — indigo inline pill (not a banner). Mirrors
       *  prototype `.preview-tax-mode`. */}
      <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
        <Info size={11} aria-hidden />
        {totals.taxMode === "intra"
          ? `Intra-state · ${seller.state ?? "—"} → ${customer.state ?? seller.state ?? "—"} · CGST + SGST applies`
          : `Inter-state · ${seller.state ?? "—"} → ${customer.state ?? "—"} · IGST applies`}
      </span>

      {/* Foot — "Payment to" on top, customer note below (muted prose,
       *  no title) so it reads as a covering memo. Mirrors prototype
       *  `.preview-foot`. Always rendered so the visual slot stays
       *  consistent across drafts; when bank info isn't configured we
       *  surface a quiet "Add in Settings" pointer instead of hiding
       *  the block. */}
      <div className="mt-4 flex flex-col gap-2.5 border-t border-line pt-3.5 text-xs leading-snug text-muted">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-muted">
            Payment to
          </span>
          <br />
          {seller.bankAccountNumber || seller.bankUpiId ? (
            <span className="font-mono text-ink-2">
              {[
                seller.bankName,
                seller.name,
                seller.bankAccountNumber ? `A/C ···${seller.bankAccountNumber.slice(-4)}` : null,
                seller.bankIfsc ? `IFSC ${seller.bankIfsc}` : null,
                seller.bankUpiId ? `UPI ${seller.bankUpiId}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          ) : (
            <span className="italic">
              Add bank details in Settings → Company profile to print them here.
            </span>
          )}
        </div>
        {customerNote ? <div className="whitespace-pre-line">{customerNote}</div> : null}
      </div>
    </div>
  );

  if (chrome === "plain") return content;
  return (
    <Card>
      <CardBody className="p-5 sm:p-6">{content}</CardBody>
    </Card>
  );
}

function PreviewMetaCell({
  label,
  children,
  mono,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-muted">{label}</p>
      <div className={["text-xs text-ink-2", mono ? "font-mono" : ""].join(" ")}>{children}</div>
    </div>
  );
}

function TotalRow({
  label,
  value,
  negative,
  signed,
}: {
  label: string;
  value: bigint;
  /** When true, prefix with `−` if the value is non-zero (Discount).
   *  Zero discounts render as plain `₹0.00`. */
  negative?: boolean;
  /** When true, prefix with `+` for positive non-zero round-offs and
   *  `−` for negative ones. Zero renders plain. */
  signed?: boolean;
}) {
  const prefix = value === 0n ? "" : negative ? "−" : signed ? (value > 0n ? "+" : "−") : "";
  const absText = formatPaise(value < 0n ? -value : value);
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-xs text-muted">
      <dt>{label}</dt>
      <dd className="font-mono font-medium tabular-nums text-ink">
        {prefix}₹{absText}
      </dd>
    </div>
  );
}

function formatPreviewDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (!Number.isFinite(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

function statusTone(status: NonNullable<InvoicePreviewProps["status"]>): StatusTone {
  switch (status) {
    case "paid":
      return "ok";
    case "overdue":
      return "bad";
    case "partially_paid":
      return "warn";
    case "void":
    case "draft":
      return "muted";
    case "issued":
    default:
      return "sky";
  }
}

function statusLabel(status: NonNullable<InvoicePreviewProps["status"]>): string {
  switch (status) {
    case "partially_paid":
      return "Partial";
    case "issued":
      return "Sent";
    case "void":
      return "Voided";
    default:
      return status[0]!.toUpperCase() + status.slice(1);
  }
}
